import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkle, CircleNotch } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

export function AiSuggest({ kind, context, onApply, testid = "ai-suggest" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const hoverCapable = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const load = async () => {
    if (result || loading) return;
    setLoading(true);
    try {
      const res = await api.post("/ai/suggest", { kind, context });
      setResult(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const show = () => {
    setOpen(true);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          onMouseEnter={hoverCapable ? show : undefined}
          onClick={show}
          className="inline-flex items-center gap-1 rounded-md border border-[#FDBA74] bg-[#FFF7ED] px-2 py-1 text-xs font-semibold text-[#C2410C] transition-colors duration-200 hover:bg-[#FFEDD5]"
        >
          <Sparkle size={13} weight="fill" /> AI
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" data-testid={`${testid}-popover`}>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">AI Suggestions</span>
          {result && !result.ai && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">offline</span>}
        </div>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500" data-testid={`${testid}-loading`}>
            <CircleNotch size={16} className="animate-spin" /> Generating…
          </div>
        ) : result ? (
          <div className="space-y-2">
            {result.items.map((item, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-2.5" data-testid={`${testid}-item-${i}`}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#C2410C]">{item.label}</div>
                <p className="mt-1 text-xs leading-relaxed text-slate-700">{item.value.length > 220 ? item.value.slice(0, 220) + "…" : item.value}</p>
                {item.extra?.category && <p className="mt-1 text-[11px] text-slate-500">Category: <b>{item.extra.category}</b></p>}
                {item.extra?.subject && <p className="mt-1 text-[11px] text-slate-500">Subject: <b>{item.extra.subject}</b></p>}
                <button
                  type="button"
                  data-testid={`${testid}-apply-${i}`}
                  onClick={() => { onApply(item); setOpen(false); }}
                  className="mt-2 rounded-md bg-[#EA580C] px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#C2410C]"
                >
                  Use this
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-4 text-xs text-slate-400">Could not load suggestions.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
